import { useTranslation } from '@/contexts/i18n-context';
import styles from './person-information-section.module.css';
import clsx from 'clsx';
import { Checkbox } from '../inputs/checkbox';
import { Form } from '../form';
import z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLoggedUser } from '@/contexts/current-user-context';
import { GetPersonalInfoResponse, GetPersonalInfoResponseSurrogate } from '@/api/api.schemas';
import { Loader } from '../loaders/loader';
import { useUpdatePatientNotifications } from '@/api/update-personal-notifications';
import { useUpdatePatientSurrogateNotifications } from '@/api/update-surrogate-notifications';
import { useState } from 'react';
import { Banner } from '../banner';

interface NotificationsInformationProps {
  patient: GetPersonalInfoResponse | undefined;
  isFetchingPatient: boolean;
  surrogate: GetPersonalInfoResponseSurrogate | undefined;
}

const DISPLAY_TIME = 3 * 1000; // 3 seconds

const translationBase = 'patientInformation.notification';

type BannerType = 'success' | 'error';

export const NotificationsSchema = z.object({
  textPatient: z.boolean(),
  textSurrogate: z.boolean(),
});

export type FormValues = z.infer<typeof NotificationsSchema>;

export function NotificationsInformation({
  patient,
  surrogate,
  isFetchingPatient,
}: NotificationsInformationProps) {
  const { t } = useTranslation();
  const { user } = useLoggedUser();
  const { mutateAsync: updatePatientNotifications } = useUpdatePatientNotifications();
  const { mutateAsync: updateSurrogateNotifications } = useUpdatePatientSurrogateNotifications();

  const [patientBannerType, setPatientBannerType] = useState<BannerType>();
  const [surrogateBannerType, setSurrogateBannerType] = useState<BannerType>();

  const form = useForm<FormValues>({
    resolver: zodResolver(NotificationsSchema),
    mode: 'onBlur',
  });

  const handlePatientChange = (checked: boolean) => {
    updatePatientNotifications(
      {
        patientId: user.id,
        data: { textMe: checked },
      },
      {
        onSuccess: () => {
          setPatientBannerType('success');
          setTimeout(() => setPatientBannerType(undefined), DISPLAY_TIME);
        },
        onError: () => {
          setPatientBannerType('error');
          setTimeout(() => setPatientBannerType(undefined), DISPLAY_TIME);
        },
      },
    );
  };

  const handleSurrogateChange = (checked: boolean) => {
    updateSurrogateNotifications(
      {
        patientId: user.id,
        data: { textMe: checked },
      },
      {
        onSuccess: () => {
          setSurrogateBannerType('success');
          setTimeout(() => setSurrogateBannerType(undefined), DISPLAY_TIME);
        },
        onError: () => {
          setSurrogateBannerType('error');
          setTimeout(() => setSurrogateBannerType(undefined), DISPLAY_TIME);
        },
      },
    );
  };

  if (isFetchingPatient) {
    return <Loader type="small" />;
  }

  return (
    <div className={styles.section}>
      <div className={styles.titleSection}>
        <p className={clsx('body-1', 'bold')}>{t(`${translationBase}.title`)}</p>
      </div>

      <p className="semibold">
        {t(`${translationBase}.description${surrogate ? 'WithSurrogate' : ''}`)}
      </p>

      {patient?.cellPhone && (
        <Form form={form} className={styles.form}>
          <Checkbox
            name="textPatient"
            label={t(`${translationBase}.checkboxes.patient`)}
            onChange={(e) => handlePatientChange(e.target.checked)}
            defaultChecked={patient.textMe ?? false}
          />

          {surrogate && (
            <Checkbox
              name="textSurrogate"
              label={t(`${translationBase}.checkboxes.surrogate`)}
              onChange={(e) => handleSurrogateChange(e.target.checked)}
              defaultChecked={surrogate.textMe ?? false}
            />
          )}

          {patientBannerType && (
            <Banner type={patientBannerType} className={styles.notificationBanner}>
              {t(`${translationBase}.banner.${patientBannerType}Patient`)}
            </Banner>
          )}
          {surrogate && surrogateBannerType && (
            <Banner type={surrogateBannerType} className={styles.notificationBanner}>
              {t(`${translationBase}.banner.${surrogateBannerType}Surrogate`)}
            </Banner>
          )}
        </Form>
      )}
    </div>
  );
}
